import * as SunCalc from 'suncalc';

export interface GalacticCorePosition {
  altitude: number;  // degrees
  azimuth: number;   // degrees (0 = North, 90 = East, etc.)
  isVisible: boolean;
  visibilityReason: 'Visible' | 'Daylight' | 'Below Horizon' | 'Moon Interference';
}

export function getEquatorialToHorizontal(
  ra: number, // degrees
  dec: number, // degrees
  lat: number, // degrees
  lon: number, // degrees
  date: Date
): { altitude: number; azimuth: number } {
  // 1. Calculate Julian Date (JD) and Centuries (d) since J2000.0
  const time = date.getTime();
  const JD = (time / 86400000) + 2440587.5;
  const d = JD - 2451545.0;

  // 2. Greenwich Mean Sidereal Time (GMST) in degrees
  let GMST = (280.46061837 + 360.98564736629 * d) % 360;
  if (GMST < 0) GMST += 360;

  // 3. Local Sidereal Time (LST) in degrees
  let LST = (GMST + lon) % 360;
  if (LST < 0) LST += 360;

  // 4. Hour Angle (HA) in degrees
  let HA = (LST - ra) % 360;
  if (HA > 180) HA -= 360;
  if (HA < -180) HA += 360;

  // Convert to radians for trigonometric calculations
  const latRad = (lat * Math.PI) / 180;
  const decRad = (dec * Math.PI) / 180;
  const haRad = (HA * Math.PI) / 180;

  // 5. Calculate Altitude (Elevation angle above horizon)
  const sinAlt = Math.sin(latRad) * Math.sin(decRad) + Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
  const altRad = Math.asin(sinAlt);
  const altitude = (altRad * 180) / Math.PI;

  // 6. Calculate Azimuth (Direction clockwise from North)
  const y = -Math.sin(haRad) * Math.cos(decRad);
  const x = Math.sin(decRad) * Math.cos(latRad) - Math.cos(decRad) * Math.sin(latRad) * Math.cos(haRad);
  let azRad = Math.atan2(y, x);
  let azimuth = (azRad * 180) / Math.PI;
  azimuth = (azimuth + 360) % 360;

  return { altitude, azimuth };
}

/**
 * Calculates the Altitude and Azimuth of the Galactic Center (Sagittarius A*)
 * for a given location and date, along with its visibility status.
 */
export function getGalacticCorePosition(lat: number, lon: number, date: Date): GalacticCorePosition {
  const RA = 266.4168; // J2000 Right Ascension of Sagittarius A* in degrees
  const Dec = -29.0078; // J2000 Declination of Sagittarius A* in degrees

  const pos = getEquatorialToHorizontal(RA, Dec, lat, lon, date);
  const altitude = pos.altitude;
  const azimuth = pos.azimuth;

  // 7. Calculate visibility conditions
  // A. Altitude check
  if (altitude <= 0) {
    return {
      altitude,
      azimuth,
      isVisible: false,
      visibilityReason: 'Below Horizon'
    };
  }

  // B. Sun altitude check for daylight/twilight
  const sunPos = SunCalc.getPosition(date, lat, lon);
  const sunAltDegrees = sunPos.altitude;
  if (sunAltDegrees > -18) {
    return {
      altitude,
      azimuth,
      isVisible: false,
      visibilityReason: 'Daylight'
    };
  }

  // C. Moon check (interferes if moon is above horizon and >30% illuminated)
  const moonIllum = SunCalc.getMoonIllumination(date);
  const moonPos = SunCalc.getMoonPosition(date, lat, lon);
  const moonAltDegrees = moonPos.altitude;
  if (moonAltDegrees > 0 && moonIllum.fraction > 0.3) {
    return {
      altitude,
      azimuth,
      isVisible: false,
      visibilityReason: 'Moon Interference'
    };
  }

  return {
    altitude,
    azimuth,
    isVisible: true,
    visibilityReason: 'Visible'
  };
}

export interface MoonPhaseDetails {
  name: string;
  icon: string;
  illumination: string;
}

export function getMoonPhaseDetails(date: Date): MoonPhaseDetails {
  const m = SunCalc.getMoonIllumination(date);
  let b = Math.round(m.phase * 8);
  if (b >= 8) b = 0;
  return {
    name: ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"][b],
    icon: ["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"][b],
    illumination: Math.round(m.fraction * 100) + "% Illumination"
  };
}

export function isFullMoon(date: Date): boolean {
  const m = SunCalc.getMoonIllumination(date);
  return m.fraction >= 0.98;
}

export function isNewMoon(date: Date): boolean {
  const m = SunCalc.getMoonIllumination(date);
  return m.fraction <= 0.03;
}

export function isSupermoon(date: Date, lat: number = 45, lon: number = -120): boolean {
  const m = SunCalc.getMoonIllumination(date);
  if (m.fraction < 0.97) return false;
  const pos = SunCalc.getMoonPosition(date, lat, lon);
  return pos.distance <= 360000;
}

export function isHarvestMoon(date: Date): boolean {
  if (!isFullMoon(date)) return false;
  const month = date.getMonth(); // 8 = Sep, 9 = Oct
  const day = date.getDate();
  return (month === 8 && day >= 10) || (month === 9 && day <= 10);
}

export function isMoonlessNight(date: Date, lat: number, lon: number): boolean {
  const sTimes = SunCalc.getTimes(date, lat, lon);
  const dusk = sTimes.night || sTimes.dusk;
  const dawn = sTimes.nightEnd || sTimes.dawn;
  if (!dusk || !dawn || isNaN(dusk.getTime()) || isNaN(dawn.getTime())) return false;

  // Sample 5 points throughout astronomical night
  const duskMs = dusk.getTime();
  const dawnMs = dawn.getTime() < duskMs ? dawn.getTime() + 86400000 : dawn.getTime();
  const stepMs = (dawnMs - duskMs) / 4;

  for (let i = 0; i <= 4; i++) {
    const sampleDate = new Date(duskMs + i * stepMs);
    const moonPos = SunCalc.getMoonPosition(sampleDate, lat, lon);
    if (moonPos.altitude > -0.83) { // moonPos.altitude is already in degrees
      return false; // Moon is above horizon during astronomical dark
    }
  }
  return true;
}

export function calculateLongTermDarkSkiesScore(bortle: number, date: Date, lat: number, lon: number): { score: number; label: string } {
  // Base score from Bortle scale (1 -> 10.0, 9 -> 1.0)
  const baseScore = Math.max(1.0, Math.min(10.0, 10.0 - (bortle - 1) * 1.1));

  const sTimes = SunCalc.getTimes(date, lat, lon);
  const refMs = date.getTime();
  const dusk = sTimes.night || sTimes.dusk || new Date(refMs + 21 * 3600 * 1000);
  const dawn = sTimes.nightEnd || sTimes.dawn || new Date(refMs + 29 * 3600 * 1000);

  const duskMs = dusk.getTime();
  const dawnMs = dawn.getTime() < duskMs ? dawn.getTime() + 86400000 : dawn.getTime();
  const stepMs = (dawnMs - duskMs) / 5;

  let maxPenalty = 0;
  for (let i = 0; i <= 5; i++) {
    const sampleDate = new Date(duskMs + i * stepMs);
    const mIllum = SunCalc.getMoonIllumination(sampleDate);
    const mPos = SunCalc.getMoonPosition(sampleDate, lat, lon);
    const moonAltDeg = mPos.altitude; // Already in degrees!

    if (moonAltDeg > 0) {
      const altFactor = Math.sin((moonAltDeg * Math.PI) / 180);
      const penalty = mIllum.fraction * altFactor * 7.0;
      if (penalty > maxPenalty) maxPenalty = penalty;
    }
  }

  const finalScore = Math.max(1.0, Math.min(10.0, Math.round((baseScore - maxPenalty) * 10) / 10));

  let label = 'Excellent';
  if (finalScore < 3.0) label = 'Poor';
  else if (finalScore < 5.5) label = 'Fair';
  else if (finalScore < 8.0) label = 'Good';

  return { score: finalScore, label };
}

export function getUtcDateForLocalTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number = 0,
  tz: string = 'America/Los_Angeles'
): Date {
  const estDate = new Date(Date.UTC(year, month, day, hour, minute, 0, 0));
  const dFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  });
  const parts = dFmt.formatToParts(estDate);
  const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);

  const estYear = getPart('year');
  const estMonth = getPart('month') - 1;
  const estDay = getPart('day');
  let estHour = getPart('hour');
  if (estHour === 24) estHour = 0;
  const estMin = getPart('minute');

  const targetMs = Date.UTC(year, month, day, hour, minute, 0, 0);
  const actualLocalMs = Date.UTC(estYear, estMonth, estDay, estHour, estMin, 0, 0);
  const diffMs = targetMs - actualLocalMs;

  return new Date(estDate.getTime() + diffMs);
}

export interface GalacticCoreTimelineSample {
  hourDec: number;
  altitude: number;
  azimuth: number;
  sunAltitudeDeg: number;
  isCoreAboveHorizon: boolean;
  isDarkNight: boolean;
  isPrimeViewing: boolean;
}

export interface GalacticCoreDayTimeline {
  hasVisibility: boolean;
  riseTimeDec: number | null; // local hour 0.0 - 24.0
  setTimeDec: number | null;
  peakTimeDec: number | null;
  peakAltitude: number;
  peakAzimuth: number;
  riseLabel: string;
  setLabel: string;
  peakLabel: string;
  primeWindowLabel: string;
  samples: GalacticCoreTimelineSample[];
}

export function formatHourDecimal(hourDec: number): string {
  const normalized = ((hourDec % 24) + 24) % 24;
  const hourInt = Math.floor(normalized);
  const minInt = Math.round((normalized - hourInt) * 60) % 60;
  const period = hourInt >= 12 ? 'PM' : 'AM';
  const displayH = hourInt % 12 === 0 ? 12 : hourInt % 12;
  const displayM = minInt.toString().padStart(2, '0');
  return `${displayH}:${displayM} ${period}`;
}

export function getGalacticCoreDayTimeline(
  lat: number,
  lon: number,
  date: Date,
  tz: string = 'America/Los_Angeles'
): GalacticCoreDayTimeline {
  const dFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
  const parts = dFmt.formatToParts(date);
  const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
  const y = getPart('year');
  const m = getPart('month') - 1;
  const d = getPart('day');

  const samples: GalacticCoreTimelineSample[] = [];
  let maxAlt = -999;
  let peakHourDec = 0;
  let peakAz = 180;
  let riseHourDec: number | null = null;
  let setHourDec: number | null = null;
  let primeStartHour: number | null = null;
  let primeEndHour: number | null = null;

  // 96 intervals = every 15 minutes across 24 hours
  const totalSteps = 96;
  for (let i = 0; i <= totalSteps; i++) {
    const hourDec = (i / totalSteps) * 24.0;
    const hourInt = Math.floor(hourDec % 24);
    const minInt = Math.round(((hourDec % 24) - hourInt) * 60) % 60;
    const sampleUtc = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);

    const core = getGalacticCorePosition(lat, lon, sampleUtc);
    const sunPos = SunCalc.getPosition(sampleUtc, lat, lon);
    const sunAlt = sunPos.altitude; // in degrees

    const isCoreAbove = core.altitude > 0;
    const isDark = sunAlt <= -18; // astronomical twilight / dark night
    const isPrime = isCoreAbove && isDark;

    if (core.altitude > maxAlt) {
      maxAlt = core.altitude;
      peakHourDec = hourDec;
      peakAz = core.azimuth;
    }

    if (i > 0) {
      const prev = samples[i - 1];
      if (!prev.isCoreAboveHorizon && isCoreAbove && riseHourDec === null) {
        riseHourDec = hourDec;
      }
      if (prev.isCoreAboveHorizon && !isCoreAbove && setHourDec === null) {
        setHourDec = hourDec;
      }
    }

    if (isPrime) {
      if (primeStartHour === null) primeStartHour = hourDec;
      primeEndHour = hourDec;
    }

    samples.push({
      hourDec,
      altitude: core.altitude,
      azimuth: core.azimuth,
      sunAltitudeDeg: sunAlt,
      isCoreAboveHorizon: isCoreAbove,
      isDarkNight: isDark,
      isPrimeViewing: isPrime
    });
  }

  const hasVisibility = maxAlt > 0;
  const riseLabel = riseHourDec !== null ? formatHourDecimal(riseHourDec) : (hasVisibility ? 'Already above horizon' : 'Does not rise');
  const setLabel = setHourDec !== null ? formatHourDecimal(setHourDec) : (hasVisibility ? 'Does not set' : 'Below horizon');
  const peakLabel = hasVisibility ? `${formatHourDecimal(peakHourDec)} (${Math.round(maxAlt)}° Alt)` : 'Below horizon';

  let primeWindowLabel = 'No dark sky visibility today';
  if (primeStartHour !== null && primeEndHour !== null) {
    if (Math.abs(primeEndHour - primeStartHour) < 0.1) {
      primeWindowLabel = `Brief dark window around ${formatHourDecimal(primeStartHour)}`;
    } else {
      primeWindowLabel = `${formatHourDecimal(primeStartHour)} – ${formatHourDecimal(primeEndHour)} (Prime Window)`;
    }
  } else if (hasVisibility) {
    primeWindowLabel = 'Visible during daylight / twilight only';
  }

  return {
    hasVisibility,
    riseTimeDec: riseHourDec,
    setTimeDec: setHourDec,
    peakTimeDec: hasVisibility ? peakHourDec : null,
    peakAltitude: maxAlt,
    peakAzimuth: peakAz,
    riseLabel,
    setLabel,
    peakLabel,
    primeWindowLabel,
    samples
  };
}



